import type {
  ArAnalysisResponse,
  CfoInsightsResponse,
  CfoContractMeta,
  GetArAnalysisOptions,
  GetCfoInsightsOptions,
  GetQuickBooksGlSummaryOptions,
  QuickBooksArCustomerConcern,
  QuickBooksArInvoiceDrilldown,
  QuickBooksArTrendAnalysis,
  QuickBooksCfoCoreBootstrapResponse,
  QuickBooksCfoPresentationResponse,
  QuickBooksCfoStatementsRenderContractResponse,
  QuickBooksFpaStatementRender,
  QuickBooksGlSummaryBucket,
  QuickBooksGlSummaryResponse,
  QuickBooksCashPlanningResponse,
  QuickBooksCashPlanningRow,
  RatioAnalysisResponse,
  RatioAssessment,
  RatioCard,
  RatioCategory,
  RatioUnit,
  MetricComparisonResponse,
  GetMetricComparisonsOptions,
  MetricComparisonKind,
  QuickBooksFpaMlStatementForecastResponse,
  QuickBooksFpaForecastGrain,
  QuickBooksFpaForecastValueCase,
  ValuationAssumptions,
  ValuationOverviewResponse,
} from './ServerApiClient'

export type AccountingCapabilityDetail = {
  available: boolean
  reason?: string | null
  provider_supported?: boolean
  permission_granted?: boolean
  source_ready?: boolean
  reconciliation_passed?: boolean
  artifact_ready?: boolean
  rollout_enabled?: boolean
  evidence?: Record<string, unknown> | null
  evaluated_at?: string | null
}

export type AccountingWorkbookBootstrap = {
  workbook_id: string
  accounting_company_id: string
  accounting_provider: string
  accounting_provider_display_name?: string | null
  accounting_company_name?: string | null
  provisioning_state?: string | null
  accounting_connection_status?: string | null
  currency_code?: string | null
  country_code?: string | null
  available_periods?: string[] | null
  default_period?: string | null
  capabilities?: Record<string, boolean> | null
  capability_details?: Record<string, AccountingCapabilityDetail> | null
  last_sync_at?: string | null
}

type AccountingStatementRow = {
  period: string
  row_key: string
  label: string
  amount: string | number
  currency_code?: string | null
  account_code?: string | null
  canonical_type?: string | null
  provider_account_type?: string | null
}

export type AccountingStatementResponse = {
  workbook_id: string
  statement: string
  rows: AccountingStatementRow[]
}

type AccountingReceivable = {
  source_id: string
  invoice_number?: string | null
  contact_name?: string | null
  invoice_date: string
  due_date: string
  outstanding_amount: string | number
  currency_code?: string | null
}

export type AccountingReceivablesResponse = {
  workbook_id: string
  invoices: AccountingReceivable[]
}

type AccountingLedgerRow = {
  provider_source_id: string
  journal_id: string
  journal_line_id: string
  transaction_date: string
  account_id: string
  account_code?: string | null
  account_name?: string | null
  description?: string | null
  debit: string | number
  credit: string | number
  currency_code?: string | null
}

export type AccountingLedgerResponse = {
  workbook_id: string
  rows: AccountingLedgerRow[]
}

export type AccountingCashResponse = {
  workbook_id: string
  weeks: Array<{
    week_start: string
    opening_cash: string | number
    cash_in: string | number
    cash_out: string | number
    closing_cash: string | number
    currency_code?: string | null
    drivers?: unknown
  }>
}

export type AccountingForecastResponse = {
  workbook_id: string
  rows: Array<{
    scenario: string
    period: string
    metric: string
    amount: string | number
    currency_code?: string | null
    method?: string | null
    lineage?: unknown
  }>
}

const numberValue = (value: unknown): number => {
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : 0
}

const nullableRatio = (numerator: number, denominator: number): number | null =>
  Math.abs(denominator) > 0.000001 ? numerator / denominator : null

const trendDirection = (values: number[]): 'improving' | 'declining' | 'stable' | 'unknown' => {
  if (values.length < 2) return 'unknown'
  const delta = values.at(-1)! - values.at(-2)!
  if (Math.abs(delta) < 0.0001) return 'stable'
  return delta > 0 ? 'improving' : 'declining'
}

const ratioAssessment = (
  value: number | null,
  min: number | null,
  max: number | null,
): RatioAssessment => {
  if (value == null) return 'neutral'
  if (min != null && value < min * 0.75) return 'concern'
  if (max != null && value > max * 1.25) return 'concern'
  if ((min != null && value < min) || (max != null && value > max)) return 'watch'
  return 'healthy'
}

const monthStart = (value: string | null | undefined): string => {
  const match = String(value || '').match(/^(\d{4})-(\d{2})/)
  return match ? `${match[1]}-${match[2]}-01` : ''
}

const monthEnd = (value: string): string => {
  const start = monthStart(value)
  if (!start) return ''
  const date = new Date(`${start}T00:00:00Z`)
  date.setUTCMonth(date.getUTCMonth() + 1)
  date.setUTCDate(0)
  return date.toISOString().slice(0, 10)
}

const daysBetween = (later: string, earlier: string): number => {
  const laterMs = Date.parse(`${later}T00:00:00Z`)
  const earlierMs = Date.parse(`${earlier}T00:00:00Z`)
  if (!Number.isFinite(laterMs) || !Number.isFinite(earlierMs)) return 0
  return Math.max(0, Math.floor((laterMs - earlierMs) / 86_400_000))
}

const contractMeta = (
  bootstrap: AccountingWorkbookBootstrap,
  contractId: string,
  rowCount: number,
  warnings: string[] = [],
): CfoContractMeta => ({
  contract_id: contractId,
  contract_version: 'accounting_canonical_v1',
  generated_at: bootstrap.last_sync_at || new Date().toISOString(),
  freshness_token: `${bootstrap.accounting_company_id}:${bootstrap.last_sync_at || 'unknown'}`,
  readiness: 'ready',
  rebuild_state: 'ready',
  execution_engine: 'postgres',
  sources: [{
    source_id: bootstrap.accounting_company_id,
    source_kind: `${bootstrap.accounting_provider}_canonical`,
    execution_engine: 'postgres',
    artifact_ids: [],
    row_count: rowCount,
    freshness_token: bootstrap.last_sync_at || null,
  }],
  warnings,
  row_count: rowCount,
})

const statementAccountMeta = (
  canonicalType: string | null | undefined,
  providerType?: string | null,
) => {
  if (String(providerType || '').toUpperCase() === 'BANK') {
    return { accountType: 'Bank', classification: 'Asset', normalBalance: 'debit' }
  }
  switch (canonicalType) {
    case 'revenue': return { accountType: 'Income', classification: 'Revenue', normalBalance: 'credit' }
    case 'cost_of_sales': return { accountType: 'Cost of Goods Sold', classification: 'Expense', normalBalance: 'debit' }
    case 'operating_expense': return { accountType: 'Expense', classification: 'Expense', normalBalance: 'debit' }
    case 'other_income': return { accountType: 'Other Income', classification: 'Revenue', normalBalance: 'credit' }
    case 'other_expense': return { accountType: 'Other Expense', classification: 'Expense', normalBalance: 'debit' }
    case 'asset': return { accountType: 'Other Current Asset', classification: 'Asset', normalBalance: 'debit' }
    case 'liability': return { accountType: 'Other Current Liability', classification: 'Liability', normalBalance: 'credit' }
    case 'equity': return { accountType: 'Equity', classification: 'Equity', normalBalance: 'credit' }
    default: return { accountType: null, classification: null, normalBalance: null }
  }
}

export function accountingBootstrapToCfoBootstrap(
  bootstrap: AccountingWorkbookBootstrap,
): QuickBooksCfoCoreBootstrapResponse {
  const companyId = bootstrap.accounting_company_id
  const layoutId = `canonical:${companyId}`
  const now = bootstrap.last_sync_at || new Date().toISOString()
  return {
    accounting_provider: bootstrap.accounting_provider,
    accounting_provider_display_name: bootstrap.accounting_provider_display_name || bootstrap.accounting_provider,
    accounting_company_id: companyId,
    accounting_connection_status: bootstrap.accounting_connection_status || null,
    currency_code: bootstrap.currency_code || null,
    country_code: bootstrap.country_code || null,
    bindings: [{
      id: `canonical-binding:${bootstrap.workbook_id}`,
      workbook_id: bootstrap.workbook_id,
      connection_id: companyId,
      layout_version_id: layoutId,
      config_json: { provider: bootstrap.accounting_provider, canonical: true },
      created_at: now,
      updated_at: now,
    }],
    connection_id: companyId,
    realm_id: companyId,
    company_name: bootstrap.accounting_company_name || null,
    layouts: [{
      id: layoutId,
      connection_id: companyId,
      realm_id: companyId,
      name: 'FuseGrid canonical layout',
      status: 'published',
      node_count: 0,
      created_at: now,
      updated_at: now,
      published_at: now,
      nodes: [],
    }],
    adjustments: [],
    selected_layout_id: layoutId,
    available_periods: bootstrap.available_periods || [],
    default_period: bootstrap.default_period || null,
    capabilities: bootstrap.capabilities || {},
    capability_details: bootstrap.capability_details || {},
    cfo_analytics_ready: bootstrap.accounting_connection_status === 'active',
    cfo_analytics_status: bootstrap.accounting_connection_status === 'active' ? 'ready' : bootstrap.accounting_connection_status || 'not_ready',
  }
}

function renderStatement(
  bootstrap: AccountingWorkbookBootstrap,
  response: AccountingStatementResponse,
  statementType: 'profit_and_loss' | 'balance_sheet' | 'cash_flow',
  asOf?: string | null,
): QuickBooksFpaStatementRender {
  const asOfMonth = monthStart(asOf)
  const sourceRows = (response.rows || []).filter((row) => !asOfMonth || monthStart(row.period) <= asOfMonth)
  const periods = Array.from(new Set(sourceRows.map((row) => monthStart(row.period)).filter(Boolean))).sort()
  const byKey = new Map<string, { source: AccountingStatementRow; label: string; values: Map<string, number> }>()
  sourceRows.forEach((row) => {
    const key = String(row.row_key || row.label)
    const entry = byKey.get(key) || { source: row, label: row.label || key, values: new Map<string, number>() }
    entry.values.set(monthStart(row.period), numberValue(row.amount))
    byKey.set(key, entry)
  })
  type StatementEntry = { nodeId: string; source: AccountingStatementRow; label: string; values: Array<number | null> }
  const entries: StatementEntry[] = Array.from(byKey.entries()).map(([nodeId, entry]) => ({
    nodeId,
    source: entry.source,
    label: entry.label,
    values: periods.map((period) => entry.values.get(period) ?? null),
  }))
  const sumSeries = (predicate: (entry: StatementEntry) => boolean): Array<number | null> =>
    periods.map((_, periodIndex) => {
      const values = entries
        .filter(predicate)
        .map((entry) => entry.values[periodIndex])
        .filter((value): value is number => typeof value === 'number' && Number.isFinite(value))
      return values.length ? values.reduce((sum, value) => sum + value, 0) : null
    })
  const combineSeries = (
    left: Array<number | null>,
    right: Array<number | null>,
    operation: (leftValue: number, rightValue: number) => number,
  ): Array<number | null> => left.map((value, index) => {
    const other = right[index]
    if (value == null && other == null) return null
    return operation(value || 0, other || 0)
  })
  const totalRow = (
    nodeId: string,
    label: string,
    values: Array<number | null>,
    options: { parent?: string | null; level?: number; cashFlowSection?: string | null; formula?: boolean; semanticKey?: string } = {},
  ) => ({
    node_id: nodeId,
    parent_node_id: options.parent ?? null,
    row_type: options.formula === false ? 'group' : 'formula',
    statement_type: statementType,
    label,
    level: options.level ?? 0,
    account_id: null,
    account_number: null,
    cash_flow_section: options.cashFlowSection ?? null,
    qbo_account_type: null,
    qbo_classification: null,
    normal_balance: null,
    values,
    is_total: true,
    is_formula: options.formula !== false,
    semantic_key: options.semanticKey || nodeId,
  })
  const headingRow = (nodeId: string, label: string, cashFlowSection?: string | null) => ({
    ...totalRow(nodeId, label, periods.map(() => null), { formula: false, cashFlowSection }),
    row_type: 'heading',
    is_total: false,
  })
  const accountRow = (entry: StatementEntry, parentNodeId: string | null, index: number, level?: number) => {
    const meta = statementAccountMeta(entry.source.canonical_type, entry.source.provider_account_type)
    return {
      node_id: entry.nodeId,
      parent_node_id: parentNodeId,
      row_type: 'account',
      statement_type: statementType,
      label: entry.label,
      level: level ?? (parentNodeId ? 1 : 0),
      account_id: entry.nodeId,
      account_number: entry.source.account_code || null,
      cash_flow_section: null,
      qbo_account_type: meta.accountType || entry.source.provider_account_type || null,
      qbo_classification: meta.classification,
      normal_balance: meta.normalBalance,
      values: entry.values,
      is_total: false,
      is_formula: false,
      sort_order: index,
    }
  }
  let rows
  if (statementType === 'profit_and_loss') {
    const canonical = (type: string) => (entry: StatementEntry) => entry.source.canonical_type === type
    const revenueEntries = entries.filter((entry) => ['revenue', 'other_income'].includes(String(entry.source.canonical_type)))
    const costEntries = entries.filter(canonical('cost_of_sales'))
    const expenseEntries = entries.filter((entry) => ['operating_expense', 'other_expense'].includes(String(entry.source.canonical_type)))
    const revenue = sumSeries((entry) => revenueEntries.includes(entry))
    const costs = sumSeries((entry) => costEntries.includes(entry))
    const expenses = sumSeries((entry) => expenseEntries.includes(entry))
    const grossProfit = combineSeries(revenue, costs, (a, b) => a - b)
    const operatingIncome = combineSeries(grossProfit, expenses, (a, b) => a - b)
    rows = [
      headingRow('income', 'Income'),
      ...revenueEntries.map((entry, index) => accountRow(entry, 'income', index)),
      totalRow('total_revenue', 'Total Revenue', revenue),
      headingRow('cost_of_goods_sold', 'Cost of Goods Sold'),
      ...costEntries.map((entry, index) => accountRow(entry, 'cost_of_goods_sold', 100 + index)),
      totalRow('total_cost_of_sales', 'Total Cost of Goods Sold', costs),
      totalRow('gross_profit', 'Gross Profit', grossProfit),
      headingRow('expenses', 'Expenses'),
      ...expenseEntries.map((entry, index) => accountRow(entry, 'expenses', 200 + index)),
      totalRow('total_operating_expenses', 'Total Expenses', expenses, { semanticKey: 'total_expenses' }),
      totalRow('operating_income', 'Operating Income', operatingIncome, { semanticKey: 'net_operating_income' }),
      totalRow('net_income', 'Net Income', operatingIncome),
    ]
  } else if (statementType === 'balance_sheet') {
    const assetEntries = entries.filter((entry) => entry.source.canonical_type === 'asset')
    const liabilityEntries = entries.filter((entry) => entry.source.canonical_type === 'liability')
    const equityEntries = entries.filter((entry) => entry.source.canonical_type === 'equity')
    // Canonical providers do not all expose a QuickBooks-style BANK type. The
    // canonical cash source ID is therefore authoritative, while BANK remains
    // supported for provider payloads that supply it.
    const isCashEntry = (entry: StatementEntry) => (
      entry.nodeId === 'cash'
      || String(entry.source.provider_account_type || '').toUpperCase() === 'BANK'
    )
    const cash = sumSeries(isCashEntry)
    const assets = sumSeries((entry) => assetEntries.includes(entry))
    const liabilities = sumSeries((entry) => liabilityEntries.includes(entry))
    const equity = sumSeries((entry) => equityEntries.includes(entry))
    const liabilitiesAndEquity = combineSeries(liabilities, equity, (a, b) => a + b)
    rows = [
      headingRow('assets', 'Assets'),
      totalRow('cash_and_cash_equivalents', 'Cash and Cash Equivalents', cash, { parent: 'assets', level: 1, semanticKey: 'cash' }),
      ...assetEntries.map((entry, index) => (
        isCashEntry(entry)
          ? accountRow(entry, 'cash_and_cash_equivalents', index, 2)
          : accountRow(entry, 'assets', index, 1)
      )),
      totalRow('total_current_assets', 'Total Current Assets', assets),
      totalRow('total_assets', 'Total Assets', assets),
      headingRow('liabilities', 'Liabilities'),
      ...liabilityEntries.map((entry, index) => accountRow(entry, 'liabilities', 100 + index)),
      totalRow('total_current_liabilities', 'Total Current Liabilities', liabilities),
      totalRow('total_liabilities', 'Total Liabilities', liabilities),
      headingRow('equity', 'Equity'),
      ...equityEntries.map((entry, index) => accountRow(entry, 'equity', 200 + index)),
      totalRow('total_equity', 'Total Equity', equity),
      totalRow('total_liabilities_equity', 'Total Liabilities and Equity', liabilitiesAndEquity),
    ]
  } else {
    const keyed = (key: string) => entries.find((entry) => entry.nodeId === key)?.values || periods.map(() => null)
    const operating = keyed('net_cash_operating')
    const investing = keyed('net_cash_investing')
    const financing = keyed('net_cash_financing')
    const netChange = keyed('net_change_cash').some((value) => value != null)
      ? keyed('net_change_cash')
      : combineSeries(combineSeries(operating, investing, (a, b) => a + b), financing, (a, b) => a + b)
    rows = [
      totalRow('beginning_cash', 'Beginning Cash', keyed('beginning_cash'), { cashFlowSection: 'cash' }),
      totalRow('net_cash_operating', 'Net Cash from Operating Activities', operating, { cashFlowSection: 'operating' }),
      totalRow('net_cash_investing', 'Net Cash from Investing Activities', investing, { cashFlowSection: 'investing' }),
      totalRow('net_cash_financing', 'Net Cash from Financing Activities', financing, { cashFlowSection: 'financing' }),
      totalRow('net_change_cash', 'Net Change in Cash', netChange, { cashFlowSection: 'cash' }),
      totalRow('ending_cash', 'Ending Cash', keyed('ending_cash'), { cashFlowSection: 'cash' }),
    ]
  }
  return {
    connection_id: bootstrap.accounting_company_id,
    layout_version_id: `canonical:${bootstrap.accounting_company_id}`,
    statement_type: statementType,
    balance_sheet_mode: statementType === 'balance_sheet' ? 'point_in_time' : 'period_activity',
    periods,
    rows,
    warnings: sourceRows.length ? [] : [`${statementType}_not_available`],
  }
}

export function accountingStatementsToRenderContract(
  bootstrap: AccountingWorkbookBootstrap,
  responses: {
    profit_and_loss: AccountingStatementResponse
    balance_sheet: AccountingStatementResponse
    cash_flow: AccountingStatementResponse
  },
  opts?: GetCfoInsightsOptions,
): QuickBooksCfoStatementsRenderContractResponse {
  const profitAndLoss = renderStatement(bootstrap, responses.profit_and_loss, 'profit_and_loss', opts?.asOf)
  const balanceSheet = renderStatement(bootstrap, responses.balance_sheet, 'balance_sheet', opts?.asOf)
  const cashFlow = renderStatement(bootstrap, responses.cash_flow, 'cash_flow', opts?.asOf)
  const periods = [...profitAndLoss.periods, ...balanceSheet.periods, ...cashFlow.periods].sort()
  const asOf = monthStart(opts?.asOf) || periods.at(-1) || monthStart(bootstrap.default_period) || ''
  const warnings = [...profitAndLoss.warnings, ...balanceSheet.warnings, ...cashFlow.warnings]
  const rowCount = profitAndLoss.rows.length + balanceSheet.rows.length + cashFlow.rows.length
  return {
    workbook_id: bootstrap.workbook_id,
    connection_id: bootstrap.accounting_company_id,
    realm_id: bootstrap.accounting_company_id,
    as_of: asOf,
    grain: 'month',
    contract: contractMeta(bootstrap, 'accounting_statements_render', rowCount, warnings),
    profit_and_loss: profitAndLoss,
    balance_sheet: balanceSheet,
    cash_flow: cashFlow,
    warnings,
  }
}

function latestStatementValue(
  statement: QuickBooksFpaStatementRender,
  predicate: (row: QuickBooksFpaStatementRender['rows'][number]) => boolean,
): number | null {
  const index = Math.max(0, statement.periods.length - 1)
  const values = statement.rows
    .filter(predicate)
    .map((row) => row.values[index])
    .filter((value): value is number => typeof value === 'number' && Number.isFinite(value))
  return values.length ? values.reduce((sum, value) => sum + value, 0) : null
}

export function accountingStatementsToPresentation(
  bootstrap: AccountingWorkbookBootstrap,
  statements: QuickBooksCfoStatementsRenderContractResponse,
  ar: ArAnalysisResponse | null,
  insights: CfoInsightsResponse,
  metricComparisons: MetricComparisonResponse,
): QuickBooksCfoPresentationResponse {
  const byClassification = (classification: string) => (
    row: QuickBooksFpaStatementRender['rows'][number],
  ) => String(row.qbo_classification || '').toLowerCase() === classification
  const byType = (...types: string[]) => (row: QuickBooksFpaStatementRender['rows'][number]) =>
    types.includes(String(row.qbo_account_type || '').toLowerCase())
  const bySemanticKey = (...keys: string[]) => (row: QuickBooksFpaStatementRender['rows'][number]) =>
    keys.includes(String(row.semantic_key || row.node_id || '').toLowerCase())
  const revenue = latestStatementValue(statements.profit_and_loss, byClassification('revenue'))
  const grossCosts = latestStatementValue(statements.profit_and_loss, byType('cost of goods sold', 'cost_of_goods_sold'))
  const expenses = latestStatementValue(statements.profit_and_loss, byType('expense', 'other expense'))
  const grossProfit = revenue == null ? null : revenue - (grossCosts || 0)
  const netIncome = grossProfit == null ? null : grossProfit - (expenses || 0)
  // Use the generated cash subtotal, not both the subtotal and its account
  // children, otherwise a one-account cash section is counted twice.
  const cashPredicate = (row: QuickBooksFpaStatementRender['rows'][number]) => (
    String(row.node_id || '').toLowerCase() === 'cash_and_cash_equivalents'
  )
  const cash = latestStatementValue(statements.balance_sheet, cashPredicate)
  const assets = latestStatementValue(statements.balance_sheet, byClassification('asset'))
  const liabilities = latestStatementValue(statements.balance_sheet, byClassification('liability'))
  const equity = latestStatementValue(statements.balance_sheet, byClassification('equity'))
  const cashIndex = Math.max(0, statements.balance_sheet.periods.length - 1)
  const priorCash = cashIndex > 0
    ? statements.balance_sheet.rows
        .filter(cashPredicate)
        .map((row) => row.values[cashIndex - 1])
        .filter((value): value is number => typeof value === 'number' && Number.isFinite(value))
        .reduce((sum, value) => sum + value, 0)
    : null
  const statementNetCashChange = latestStatementValue(
    statements.cash_flow,
    bySemanticKey('net_change_cash', 'net_cash_change'),
  )
  const statementEndingCash = latestStatementValue(
    statements.cash_flow,
    bySemanticKey('ending_cash'),
  )
  const netCashChange = statementNetCashChange ?? (
    cash == null || priorCash == null ? null : cash - priorCash
  )
  const ratio = (numerator: number | null, denominator: number | null) =>
    numerator == null || denominator == null || denominator === 0 ? null : numerator / denominator
  const signature = `accounting-presentation:${bootstrap.accounting_company_id}:${statements.as_of}`
  return {
    workbook_id: bootstrap.workbook_id,
    binding_id: `accounting:${bootstrap.accounting_company_id}`,
    connection_id: bootstrap.accounting_company_id,
    layout_version_id: `canonical:${bootstrap.accounting_company_id}`,
    company_name: bootstrap.accounting_company_name || null,
    layout_name: 'FuseGrid canonical layout',
    can_edit: false,
    config: null,
    metrics: {
      profitAndLoss: {
        totalRevenue: revenue,
        grossProfit,
        netIncome,
        ebitda: netIncome,
        operatingExpenses: expenses,
        grossMarginPct: ratio(grossProfit, revenue),
        netMarginPct: ratio(netIncome, revenue),
      },
      balanceSheet: {
        cash,
        totalAssets: assets,
        totalLiabilities: liabilities,
        totalEquity: equity,
        debtToAssetsPct: ratio(liabilities, assets),
      },
      cashFlow: { netCashChange, endingCash: statementEndingCash ?? cash },
    },
    ar_analysis: ar?.ar_analysis || null,
    statements: {
      profit_and_loss: statements.profit_and_loss,
      balance_sheet: statements.balance_sheet,
      cash_flow: statements.cash_flow,
    },
    warnings: statements.warnings,
    insights,
    metric_comparisons: metricComparisons,
    ratio_cards: null,
    source_state: {
      signature,
      reporting_period: statements.as_of,
      data_through: statements.as_of,
      last_successful_refresh: bootstrap.last_sync_at || null,
      reconciliation_status: 'passed',
      close_readiness_status: bootstrap.capabilities?.close ? 'needs_review' : 'unavailable',
      forecast_included: Boolean(bootstrap.capabilities?.forecast),
      new_data_available: false,
      review_state: 'unreviewed',
    },
    generated_at: new Date().toISOString(),
  }
}

export function accountingReceivablesToArAnalysis(
  bootstrap: AccountingWorkbookBootstrap,
  response: AccountingReceivablesResponse,
  opts?: GetArAnalysisOptions,
): ArAnalysisResponse {
  const requestedMonth = monthStart(opts?.asOf) || monthStart(bootstrap.default_period) || monthStart(new Date().toISOString())
  const asOfDate = monthEnd(requestedMonth)
  const invoices = (response.invoices || []).filter((invoice) => !asOfDate || invoice.invoice_date <= asOfDate)
  const total = invoices.reduce((sum, invoice) => sum + numberValue(invoice.outstanding_amount), 0)
  const buckets = new Map<string, number>([['Current', 0], ['1–30', 0], ['31–60', 0], ['61–90', 0], ['90+', 0]])
  const customerMap = new Map<string, QuickBooksArCustomerConcern>()
  const drilldowns: QuickBooksArInvoiceDrilldown[] = invoices.map((invoice) => {
    const amount = numberValue(invoice.outstanding_amount)
    const daysOpen = daysBetween(asOfDate, invoice.invoice_date)
    const daysPastDue = invoice.due_date < asOfDate ? daysBetween(asOfDate, invoice.due_date) : 0
    const bucket = daysPastDue <= 0 ? 'Current' : daysPastDue <= 30 ? '1–30' : daysPastDue <= 60 ? '31–60' : daysPastDue <= 90 ? '61–90' : '90+'
    buckets.set(bucket, (buckets.get(bucket) || 0) + amount)
    const customer = invoice.contact_name || 'Unknown customer'
    const concern = customerMap.get(customer) || {
      customer,
      endingAr: 0,
      overdueAr: 0,
      over60Ar: 0,
      over90Ar: 0,
      weightedAverageAge: 0,
      openInvoiceCount: 0,
    }
    concern.endingAr = numberValue(concern.endingAr) + amount
    concern.overdueAr = numberValue(concern.overdueAr) + (daysPastDue > 0 ? amount : 0)
    concern.over60Ar = numberValue(concern.over60Ar) + (daysPastDue > 60 ? amount : 0)
    concern.over90Ar = numberValue(concern.over90Ar) + (daysPastDue > 90 ? amount : 0)
    concern.openInvoiceCount = numberValue(concern.openInvoiceCount) + 1
    concern.weightedAverageAge = numberValue(concern.weightedAverageAge) + daysOpen * amount
    customerMap.set(customer, concern)
    return {
      invoiceKey: invoice.source_id,
      periodStart: requestedMonth,
      periodEnd: asOfDate,
      customer,
      invoiceNumber: invoice.invoice_number || null,
      invoiceDate: invoice.invoice_date,
      dueDate: invoice.due_date,
      dueDateSource: bootstrap.accounting_provider,
      originalAmount: amount,
      openAmount: amount,
      daysOpen,
      daysPastDue,
      allocationMethod: 'provider_open_receivable',
    }
  })
  const customers = Array.from(customerMap.values()).map((customer) => ({
    ...customer,
    weightedAverageAge: numberValue(customer.endingAr) > 0
      ? numberValue(customer.weightedAverageAge) / numberValue(customer.endingAr)
      : 0,
  })).sort((a, b) => numberValue(b.overdueAr) - numberValue(a.overdueAr) || numberValue(b.endingAr) - numberValue(a.endingAr))
  const overdue = drilldowns.reduce((sum, invoice) => sum + (numberValue(invoice.daysPastDue) > 0 ? numberValue(invoice.openAmount) : 0), 0)
  const over60 = drilldowns.reduce((sum, invoice) => sum + (numberValue(invoice.daysPastDue) > 60 ? numberValue(invoice.openAmount) : 0), 0)
  const over90 = drilldowns.reduce((sum, invoice) => sum + (numberValue(invoice.daysPastDue) > 90 ? numberValue(invoice.openAmount) : 0), 0)
  const weightedAge = total > 0
    ? drilldowns.reduce((sum, invoice) => sum + numberValue(invoice.daysOpen) * numberValue(invoice.openAmount), 0) / total
    : 0
  const period = {
    period: requestedMonth.slice(0, 7),
    periodStart: requestedMonth,
    periodEnd: asOfDate,
    endingAr: total,
    invoiceEndingAr: total,
    authoritativeEndingAr: total,
    endingArSource: bootstrap.accounting_provider,
    currentAr: buckets.get('Current') || 0,
    under90Ar: total - over90,
    over90Ar: over90,
    agingBucketTotalAr: total,
    overdueAr: overdue,
    over60Ar: over60,
    weightedAverageAge: weightedAge,
  }
  const analysis: QuickBooksArTrendAnalysis = {
    asOfDate,
    periodGrain: 'month',
    topCustomerLimit: 10,
    periods: [period],
    customerTrends: customers,
    topCustomerConcerns: customers.slice(0, 10),
    invoiceDrilldowns: drilldowns,
    periodInvoiceDrilldowns: drilldowns,
    periodIncomeAccountAllocations: [],
    warnings: [],
    methodology: { source: 'fg_open_receivable', provider: bootstrap.accounting_provider },
  }
  return {
    workbook_id: bootstrap.workbook_id,
    as_of: requestedMonth,
    contract: contractMeta(bootstrap, 'accounting_ar_analysis', invoices.length),
    ar_analysis: analysis,
    receivable_total: total,
    receivable_prior: null,
    dso: null,
    dso_prior: null,
    dso_delta: null,
    buckets: Array.from(buckets, ([label, amount]) => ({ label, amount })),
    signature: `accounting-ar:${bootstrap.accounting_company_id}:${asOfDate}`,
    warnings: [],
  }
}

export function accountingLedgerToGlSummary(
  bootstrap: AccountingWorkbookBootstrap,
  response: AccountingLedgerResponse,
  opts?: GetQuickBooksGlSummaryOptions,
): QuickBooksGlSummaryResponse {
  const asOfMonth = monthStart(opts?.asOf)
  const search = String(opts?.search || '').trim().toLowerCase()
  const filtered = (response.rows || []).filter((row) => {
    if (asOfMonth && monthStart(row.transaction_date) !== asOfMonth) return false
    if (opts?.accountId && row.account_id !== opts.accountId) return false
    if (search && ![row.description, row.account_id, row.account_code, row.account_name, row.journal_id, row.provider_source_id].some((value) => String(value || '').toLowerCase().includes(search))) return false
    return true
  })
  const limit = Math.max(1, Math.floor(Number(opts?.limit) || 500))
  const rows = filtered.slice(0, limit).map((row) => {
    const debit = numberValue(row.debit)
    const credit = numberValue(row.credit)
    return {
      id: row.journal_line_id,
      source_id: row.provider_source_id,
      transaction_id: row.journal_id,
      txn_date: row.transaction_date,
      transaction_date: row.transaction_date,
      account_id: row.account_id,
      account_number: row.account_code || '',
      account: row.account_name || row.account_id,
      account_name: row.account_name || row.account_id,
      memo: row.description || '',
      description: row.description || '',
      debit: String(debit),
      credit: String(credit),
      amount: String(debit - credit),
      currency_code: row.currency_code || bootstrap.currency_code || '',
      provider: bootstrap.accounting_provider,
    }
  })
  const accountTotals = new Map<string, { amount: number; count: number }>()
  let totalDebit = 0
  let totalCredit = 0
  filtered.forEach((row) => {
    const debit = numberValue(row.debit)
    const credit = numberValue(row.credit)
    totalDebit += debit
    totalCredit += credit
    const accountLabel = row.account_name || row.account_code || row.account_id
    const account = accountTotals.get(accountLabel) || { amount: 0, count: 0 }
    account.amount += debit - credit
    account.count += 1
    accountTotals.set(accountLabel, account)
  })
  const topAccounts: QuickBooksGlSummaryBucket[] = Array.from(accountTotals, ([label, value]) => ({ label, ...value }))
    .sort((a, b) => Math.abs(b.amount) - Math.abs(a.amount))
    .slice(0, 10)
  return {
    workbook_id: bootstrap.workbook_id,
    connection_id: bootstrap.accounting_company_id,
    contract: contractMeta(bootstrap, 'accounting_gl_summary', filtered.length),
    as_of: asOfMonth || null,
    statement_type: opts?.statementType || null,
    scan_row_count: response.rows?.length || 0,
    row_count: filtered.length,
    returned_row_limit: limit,
    limited: filtered.length > limit,
    totals: { debit: totalDebit, credit: totalCredit, amount: totalDebit - totalCredit },
    top_accounts: topAccounts,
    top_vendors: [],
    top_classes: [],
    rows,
    warnings: [],
  }
}

export function accountingCashToPlanning(
  bootstrap: AccountingWorkbookBootstrap,
  response: AccountingCashResponse,
): QuickBooksCashPlanningResponse {
  const weeks = [...(response.weeks || [])].sort((a, b) => a.week_start.localeCompare(b.week_start))
  const rows: QuickBooksCashPlanningRow[] = []
  weeks.forEach((week, index) => {
    const inflow = numberValue(week.cash_in)
    const outflow = numberValue(week.cash_out)
    rows.push({
      id: `canonical-cash-in-${week.week_start}`,
      row_kind: 'ml_forecast',
      scenario_key: 'base',
      week_start: week.week_start,
      expected_date: week.week_start,
      metric_key: 'collections',
      category_key: 'collections',
      category_label: 'Collections',
      cash_table_row_key: 'collections',
      cash_table_row_label: 'Collections',
      provenance: 'modeled',
      event_type: 'receivable_collection',
      label: 'Expected collections',
      expected_amount: String(inflow),
      forecast_basis: 'single-company cash schedule',
      assumption_summary: 'Trailing company run-rate plus known open receivables.',
      editable: false,
      material: inflow !== 0,
      source_type: 'canonical_cash_forecast',
      source_key: `cash-in:${week.week_start}`,
      evidence_json: week.drivers || {},
    })
    rows.push({
      id: `canonical-cash-out-${week.week_start}`,
      row_kind: 'ml_forecast',
      scenario_key: 'base',
      week_start: week.week_start,
      expected_date: week.week_start,
      metric_key: 'vendors_ap',
      category_key: 'vendors_ap',
      category_label: 'Vendors / AP',
      cash_table_row_key: 'vendors_ap',
      cash_table_row_label: 'Vendors / AP',
      provenance: 'modeled',
      event_type: 'payable_disbursement',
      label: 'Expected disbursements',
      expected_amount: String(outflow),
      forecast_basis: 'single-company cash schedule',
      assumption_summary: 'Trailing company cost run-rate plus known open payables.',
      editable: false,
      material: outflow !== 0,
      source_type: 'canonical_cash_forecast',
      source_key: `cash-out:${week.week_start}`,
      evidence_json: week.drivers || {},
    })
  })
  const generatedAt = bootstrap.last_sync_at || new Date().toISOString()
  const opening = weeks[0] ? String(weeks[0].opening_cash) : null
  return {
    contract: contractMeta(bootstrap, 'accounting_cash_planning', rows.length),
    warnings: [],
    package: {
      id: `canonical-cash:${bootstrap.accounting_company_id}`,
      status: 'ready',
      scenario_key: 'base',
      active_scenario_key: 'base',
      start_period: weeks[0]?.week_start || null,
      updated_at: generatedAt,
    },
    scenario_key: 'base',
    as_of_date: weeks[0]?.week_start || null,
    cash_position: {
      status: weeks.length ? 'ready' : 'blocking',
      message: weeks.length ? 'Opening cash is sourced from the latest reconciled provider bank balance.' : 'A reconciled bank balance is required.',
      as_of_date: weeks[0]?.week_start || monthStart(bootstrap.default_period) || '',
      balance_period: monthStart(weeks[0]?.week_start) || null,
      ending_cash: opening,
      source: `${bootstrap.accounting_provider}_bank_balance`,
    },
    weeks: weeks.map((week, index) => {
      const openingCash = numberValue(week.opening_cash)
      const cashIn = numberValue(week.cash_in)
      const cashOut = numberValue(week.cash_out)
      const closingCash = numberValue(week.closing_cash)
      return {
        week_start: week.week_start,
        week_status: index === 0 ? 'current' : 'forecast',
        opening_cash: String(openingCash),
        receipts: String(cashIn),
        disbursements: String(cashOut),
        net_cash_change: String(cashIn - cashOut),
        ending_cash: String(closingCash),
        ending_cash_low: null,
        ending_cash_high: null,
        bridge_amount: '0',
        reviewed: false,
        needs_review: false,
        rollup_json: { provider: bootstrap.accounting_provider },
        lineage_json: week.drivers || {},
      }
    }),
    rows,
    expectations: [],
    bridge_rows: [],
    source_rows: [],
    source_summary: {
      ar_count: 0,
      ap_count: 0,
      ar_open_balance: '0',
      ap_open_balance: '0',
    },
    scenarios: ['base'],
    scenario_summary: {
      scenarios: [],
      active_scenario_key: 'base',
    },
    min_cash_floor: null,
    permissions: {
      can_edit: false,
      can_publish_cash_forecast: false,
    },
    audit_summary: { event_count: 0 },
    assistant_state: { open_question_count: 0 },
    review_prompt: {
      review_week_start: weeks[0]?.week_start || '',
      status: 'not_required',
      open_items: 0,
    },
    advanced_actions: [],
  }
}

export function accountingCashToMetricComparisons(
  bootstrap: AccountingWorkbookBootstrap,
  response: AccountingCashResponse,
  opts?: GetMetricComparisonsOptions,
): MetricComparisonResponse {
  const weeks = [...(response.weeks || [])].sort((a, b) => a.week_start.localeCompare(b.week_start))
  const first = weeks[0]
  const last = weeks.at(-1)
  const current = first ? numberValue(first.opening_cash) : null
  const projected = last ? numberValue(last.closing_cash) : null
  const comparisonKinds: MetricComparisonKind[] = opts?.comparisons?.length ? opts.comparisons : ['mom']
  return {
    workbook_id: bootstrap.workbook_id,
    connection_id: bootstrap.accounting_company_id,
    realm_id: bootstrap.accounting_company_id,
    as_of: monthStart(opts?.asOf) || monthStart(first?.week_start) || monthStart(bootstrap.default_period) || '',
    grain: 'month',
    registry_version: 'accounting_canonical_v1',
    signature: `accounting-cash:${bootstrap.accounting_company_id}:${first?.week_start || 'empty'}`,
    comparisons: comparisonKinds,
    metrics: [{
      id: 'cash',
      label: 'Cash',
      statement: 'balance_sheet',
      value_kind: 'currency',
      latest_value: current,
      latest_pct_of_sales: null,
      trend: weeks.map((week) => ({
        period: week.week_start,
        label: week.week_start,
        value: numberValue(week.closing_cash),
        complete: true,
      })),
      comparisons: comparisonKinds.map((kind) => ({
        kind,
        label: kind === 'mom' ? '13-week outlook' : 'Not available from the current cash horizon',
        current_period_start: first?.week_start || null,
        current_period_end: last?.week_start || null,
        prior_period_start: null,
        prior_period_end: null,
        current_value: projected,
        prior_value: current,
        delta_abs: current != null && projected != null ? projected - current : null,
        delta_pct: current != null && projected != null && Math.abs(current) > 0.000001 ? (projected - current) / Math.abs(current) : null,
        current_pct_of_sales: null,
        prior_pct_of_sales: null,
        delta_pct_of_sales_pts: null,
        complete: kind === 'mom',
      })),
    }],
    warnings: weeks.length ? [] : ['cash_forecast_not_ready'],
  }
}

type CanonicalPeriodMetrics = {
  period: string
  revenue: number
  directCosts: number
  operatingExpenses: number
  assets: number
  liabilities: number
}

const canonicalPeriodMetrics = (
  profitAndLoss: AccountingStatementResponse,
  balanceSheet: AccountingStatementResponse,
): CanonicalPeriodMetrics[] => {
  const byPeriod = new Map<string, CanonicalPeriodMetrics>()
  const metric = (period: string) => {
    const key = monthStart(period)
    const existing = byPeriod.get(key)
    if (existing) return existing
    const created = { period: key, revenue: 0, directCosts: 0, operatingExpenses: 0, assets: 0, liabilities: 0 }
    byPeriod.set(key, created)
    return created
  }
  for (const row of profitAndLoss.rows || []) {
    const target = metric(row.period)
    const amount = numberValue(row.amount)
    if (row.canonical_type === 'revenue') target.revenue += amount
    else if (row.canonical_type === 'cost_of_sales') target.directCosts += amount
    else if (row.canonical_type === 'operating_expense') target.operatingExpenses += amount
  }
  for (const row of balanceSheet.rows || []) {
    const target = metric(row.period)
    const amount = numberValue(row.amount)
    if (row.canonical_type === 'asset') target.assets += amount
    else if (row.canonical_type === 'liability') target.liabilities += amount
  }
  return [...byPeriod.values()].filter((period) => period.period).sort((a, b) => a.period.localeCompare(b.period))
}

export function accountingStatementsToRatios(
  bootstrap: AccountingWorkbookBootstrap,
  responses: {
    profit_and_loss: AccountingStatementResponse
    balance_sheet: AccountingStatementResponse
  },
  receivables?: AccountingReceivablesResponse | null,
  asOf?: string | null,
): RatioAnalysisResponse {
  const requested = monthStart(asOf)
  const periods = canonicalPeriodMetrics(responses.profit_and_loss, responses.balance_sheet)
    .filter((period) => !requested || period.period <= requested)
  const latestPeriod = periods.at(-1)?.period || monthStart(bootstrap.default_period) || ''
  const openAr = (receivables?.invoices || []).reduce((sum, row) => sum + numberValue(row.outstanding_amount), 0)
  const definitions: Array<{
    id: string
    label: string
    category: RatioCategory
    unit: RatioUnit
    formula: string
    description: string
    higher: boolean | null
    min: number | null
    max: number | null
    value: (period: CanonicalPeriodMetrics) => number | null
  }> = [
    { id: 'current_ratio', label: 'Current Ratio', category: 'liquidity', unit: 'times', formula: 'Current Assets ÷ Current Liabilities', description: 'Ability to cover short-term obligations with current assets.', higher: true, min: 1.2, max: null, value: (p) => nullableRatio(p.assets, p.liabilities) },
    { id: 'quick_ratio', label: 'Quick Ratio', category: 'liquidity', unit: 'times', formula: 'Liquid Current Assets ÷ Current Liabilities', description: 'Near-term liquidity excluding inventory when identifiable.', higher: true, min: 1, max: null, value: (p) => nullableRatio(p.assets, p.liabilities) },
    { id: 'debt_to_equity', label: 'Debt to Equity', category: 'leverage', unit: 'times', formula: 'Total Liabilities ÷ Equity', description: 'Liabilities carried for each unit of owner equity.', higher: false, min: null, max: 2, value: (p) => nullableRatio(p.liabilities, p.assets - p.liabilities) },
    { id: 'debt_to_assets', label: 'Debt to Assets', category: 'leverage', unit: 'ratio', formula: 'Total Liabilities ÷ Total Assets', description: 'Share of assets financed by liabilities.', higher: false, min: null, max: 0.6, value: (p) => nullableRatio(p.liabilities, p.assets) },
    { id: 'days_sales_outstanding', label: 'Days Sales Outstanding (DSO)', category: 'efficiency', unit: 'days', formula: 'Open Receivables ÷ Monthly Revenue × 30.4', description: 'Approximate collection days based on current open receivables.', higher: false, min: null, max: 45, value: (p) => nullableRatio(openAr * 30.4, p.revenue) },
    { id: 'gross_margin', label: 'Gross Margin', category: 'profitability', unit: 'ratio', formula: 'Gross Profit ÷ Revenue', description: 'Share of each sales dollar left after direct costs.', higher: true, min: 0.3, max: null, value: (p) => nullableRatio(p.revenue - p.directCosts, p.revenue) },
    { id: 'operating_margin', label: 'Operating Margin', category: 'profitability', unit: 'ratio', formula: 'Operating Profit ÷ Revenue', description: 'Profitability from core operations.', higher: true, min: 0.1, max: null, value: (p) => nullableRatio(p.revenue - p.directCosts - p.operatingExpenses, p.revenue) },
    { id: 'net_margin', label: 'Net Margin', category: 'profitability', unit: 'ratio', formula: 'Net Income ÷ Revenue', description: 'Bottom-line profit per sales dollar.', higher: true, min: 0.08, max: null, value: (p) => nullableRatio(p.revenue - p.directCosts - p.operatingExpenses, p.revenue) },
    { id: 'return_on_assets', label: 'Return on Assets (ROA)', category: 'profitability', unit: 'ratio', formula: 'Annualized Net Income ÷ Total Assets', description: 'How efficiently assets produce profit.', higher: true, min: 0.08, max: null, value: (p) => nullableRatio((p.revenue - p.directCosts - p.operatingExpenses) * 12, p.assets) },
    { id: 'return_on_equity', label: 'Return on Equity (ROE)', category: 'profitability', unit: 'ratio', formula: 'Annualized Net Income ÷ Equity', description: 'Return owners earn on their equity.', higher: true, min: 0.12, max: null, value: (p) => nullableRatio((p.revenue - p.directCosts - p.operatingExpenses) * 12, p.assets - p.liabilities) },
  ]
  const cards: RatioCard[] = definitions.map((definition) => {
    const trend = periods
      .map((period) => ({ period: period.period, value: definition.value(period) }))
      .filter((point): point is { period: string; value: number } => point.value != null && Number.isFinite(point.value))
    const latest = trend.at(-1)?.value ?? null
    const prior = trend.at(-2)?.value ?? null
    const delta = latest != null && prior != null ? latest - prior : null
    return {
      id: definition.id,
      label: definition.label,
      category: definition.category,
      unit: definition.unit,
      description: definition.description,
      formula: definition.formula,
      higher_is_better: definition.higher,
      benchmark: {
        healthy_min: definition.min,
        healthy_max: definition.max,
        basis: 'healthy_band',
        note: 'FuseGrid healthy band; use an approved industry profile when available.',
      },
      latest,
      latest_period: trend.at(-1)?.period || latestPeriod || null,
      trend,
      trend_direction: trendDirection(trend.map((point) => point.value)),
      assessment: ratioAssessment(latest, definition.min, definition.max),
      comparisons: [{
        kind: 'mom',
        label: 'vs. prior month',
        current: latest,
        prior,
        delta_abs: delta,
        delta_pct: delta != null && prior != null && Math.abs(prior) > 0.000001 ? delta / Math.abs(prior) : null,
      }],
      components: [],
      interpretation: latest == null ? 'Not enough reconciled source data yet.' : `${definition.label} is calculated from the reconciled canonical statements.`,
      ai_interpretation: null,
    }
  })
  const categoryLabels: Record<RatioCategory, string> = {
    liquidity: 'Liquidity',
    leverage: 'Leverage',
    efficiency: 'Efficiency',
    profitability: 'Profitability',
    coverage: 'Coverage',
  }
  const categories = (Object.keys(categoryLabels) as RatioCategory[])
    .map((category) => ({ category, label: categoryLabels[category], ratios: cards.filter((card) => card.category === category) }))
    .filter((group) => group.ratios.length > 0)
  return {
    workbook_id: bootstrap.workbook_id,
    connection_id: bootstrap.accounting_company_id,
    realm_id: bootstrap.accounting_company_id,
    as_of: latestPeriod,
    grain: 'month',
    contract: contractMeta(bootstrap, 'accounting_ratios', cards.length),
    ratio_set_version: 'accounting_canonical_v1',
    signature: `accounting-ratios:${bootstrap.accounting_company_id}:${latestPeriod}`,
    categories,
    warnings: periods.length < 2 ? ['limited_history'] : [],
    health: null,
  }
}

export function accountingStatementsToMetricComparisons(
  bootstrap: AccountingWorkbookBootstrap,
  responses: {
    profit_and_loss: AccountingStatementResponse
    balance_sheet: AccountingStatementResponse
  },
  opts?: GetMetricComparisonsOptions,
): MetricComparisonResponse {
  const requested = monthStart(opts?.asOf)
  const periods = canonicalPeriodMetrics(responses.profit_and_loss, responses.balance_sheet)
    .filter((period) => !requested || period.period <= requested)
  const cashByPeriod = new Map<string, number>()
  for (const row of responses.balance_sheet.rows || []) {
    const label = String(row.label || '').toLowerCase()
    const isCash = row.provider_account_type?.toUpperCase() === 'BANK' || /(^|\s)(bank|cash)(\s|$)/.test(label)
    if (isCash) cashByPeriod.set(monthStart(row.period), (cashByPeriod.get(monthStart(row.period)) || 0) + numberValue(row.amount))
  }
  const metricDefinitions: Record<string, {
    label: string
    statement: 'profit_and_loss' | 'balance_sheet'
    valueKind: 'currency' | 'ratio'
    stock?: boolean
    value: (period: CanonicalPeriodMetrics) => number | null
  }> = {
    revenue: { label: 'Revenue', statement: 'profit_and_loss', valueKind: 'currency', value: (p) => p.revenue },
    gross_profit: { label: 'Gross Profit', statement: 'profit_and_loss', valueKind: 'currency', value: (p) => p.revenue - p.directCosts },
    operating_expenses: { label: 'Operating Expenses', statement: 'profit_and_loss', valueKind: 'currency', value: (p) => p.operatingExpenses },
    net_income: { label: 'Net Income', statement: 'profit_and_loss', valueKind: 'currency', value: (p) => p.revenue - p.directCosts - p.operatingExpenses },
    gross_margin: { label: 'Gross Margin', statement: 'profit_and_loss', valueKind: 'ratio', value: (p) => nullableRatio(p.revenue - p.directCosts, p.revenue) },
    net_margin: { label: 'Net Margin', statement: 'profit_and_loss', valueKind: 'ratio', value: (p) => nullableRatio(p.revenue - p.directCosts - p.operatingExpenses, p.revenue) },
    total_assets: { label: 'Total Assets', statement: 'balance_sheet', valueKind: 'currency', stock: true, value: (p) => p.assets },
    total_liabilities: { label: 'Total Liabilities', statement: 'balance_sheet', valueKind: 'currency', stock: true, value: (p) => p.liabilities },
    cash: { label: 'Cash', statement: 'balance_sheet', valueKind: 'currency', stock: true, value: (p) => cashByPeriod.get(p.period) ?? null },
    ending_cash: { label: 'Cash', statement: 'balance_sheet', valueKind: 'currency', stock: true, value: (p) => cashByPeriod.get(p.period) ?? null },
  }
  const metricIds = opts?.metrics?.length ? opts.metrics : Object.keys(metricDefinitions)
  const kinds: MetricComparisonKind[] = opts?.comparisons?.length ? opts.comparisons : ['mom', 'yoy', 'ytd', 'l3m_vs_prior3m']
  const latestIndex = periods.length - 1
  const valueAt = (definition: (typeof metricDefinitions)[string], index: number) =>
    index >= 0 && index < periods.length ? definition.value(periods[index]) : null
  const windowValue = (definition: (typeof metricDefinitions)[string], start: number, end: number): number | null => {
    const values = periods.slice(Math.max(0, start), Math.max(0, end)).map(definition.value).filter((value): value is number => value != null)
    if (!values.length) return null
    return definition.stock ? values.at(-1)! : values.reduce((sum, value) => sum + value, 0)
  }
  const rows = metricIds.flatMap((id) => {
    const definition = metricDefinitions[id]
    if (!definition) return []
    const latest = valueAt(definition, latestIndex)
    const comparisons = kinds.map((kind) => {
      let current: number | null = latest
      let prior: number | null = null
      let complete = false
      if (kind === 'mom') {
        prior = valueAt(definition, latestIndex - 1)
        complete = prior != null
      } else if (kind === 'yoy') {
        prior = valueAt(definition, latestIndex - 12)
        complete = prior != null
      } else if (kind === 'l3m_vs_prior3m' || kind === 'qoq') {
        current = windowValue(definition, latestIndex - 2, latestIndex + 1)
        prior = windowValue(definition, latestIndex - 5, latestIndex - 2)
        complete = latestIndex >= 5
      } else if (kind === 'l12m_vs_prior12m') {
        current = windowValue(definition, latestIndex - 11, latestIndex + 1)
        prior = windowValue(definition, latestIndex - 23, latestIndex - 11)
        complete = latestIndex >= 23
      } else if (kind === 'ytd') {
        const year = Number(periods.at(-1)?.period.slice(0, 4))
        const month = Number(periods.at(-1)?.period.slice(5, 7))
        const currentPeriods = periods.filter((period) => Number(period.period.slice(0, 4)) === year && Number(period.period.slice(5, 7)) <= month)
        const priorPeriods = periods.filter((period) => Number(period.period.slice(0, 4)) === year - 1 && Number(period.period.slice(5, 7)) <= month)
        const roll = (source: CanonicalPeriodMetrics[]) => {
          const values = source.map(definition.value).filter((value): value is number => value != null)
          if (!values.length) return null
          return definition.stock ? values.at(-1)! : values.reduce((sum, value) => sum + value, 0)
        }
        current = roll(currentPeriods)
        prior = roll(priorPeriods)
        complete = priorPeriods.length === currentPeriods.length && currentPeriods.length > 0
      }
      const delta = current != null && prior != null ? current - prior : null
      return {
        kind,
        label: kind,
        current_period_start: periods.at(-1)?.period || null,
        current_period_end: periods.at(-1)?.period || null,
        prior_period_start: null,
        prior_period_end: null,
        current_value: current,
        prior_value: prior,
        delta_abs: delta,
        delta_pct: delta != null && prior != null && Math.abs(prior) > 0.000001 ? delta / Math.abs(prior) : null,
        current_pct_of_sales: null,
        prior_pct_of_sales: null,
        delta_pct_of_sales_pts: null,
        complete,
      }
    })
    return [{
      id,
      label: definition.label,
      statement: definition.statement,
      value_kind: definition.valueKind,
      latest_value: latest,
      latest_pct_of_sales: definition.statement === 'profit_and_loss' && id !== 'revenue'
        ? nullableRatio(latest || 0, periods.at(-1)?.revenue || 0)
        : id === 'revenue' ? 1 : null,
      trend: periods.map((period) => ({
        period: period.period,
        label: period.period,
        value: definition.value(period),
        complete: true,
      })),
      comparisons,
    }]
  })
  return {
    workbook_id: bootstrap.workbook_id,
    connection_id: bootstrap.accounting_company_id,
    realm_id: bootstrap.accounting_company_id,
    as_of: periods.at(-1)?.period || monthStart(bootstrap.default_period) || '',
    grain: 'month',
    registry_version: 'accounting_canonical_v1',
    signature: `accounting-metrics:${bootstrap.accounting_company_id}:${periods.at(-1)?.period || 'empty'}`,
    comparisons: kinds,
    metrics: rows,
    warnings: periods.length < 2 ? ['limited_history'] : [],
  }
}

export function accountingForecastToStatementForecast(
  bootstrap: AccountingWorkbookBootstrap,
  response: AccountingForecastResponse,
  req?: {
    scenario_key?: string | null
    grain?: QuickBooksFpaForecastGrain | string | null
    statement_type?: 'profit_and_loss' | 'balance_sheet' | 'cash_flow' | string | null
    value_case?: QuickBooksFpaForecastValueCase | string | null
    as_of_date?: string | null
    future_months?: number | null
  },
): QuickBooksFpaMlStatementForecastResponse {
  const scenario = req?.scenario_key || 'base'
  const statementType = req?.statement_type || 'profit_and_loss'
  const grain = req?.grain || 'month'
  const valueCase = req?.value_case || 'expected'
  const metricOrder: Record<string, string[]> = {
    profit_and_loss: ['revenue', 'gross_profit', 'net_income'],
    cash_flow: ['net_cash_change'],
    balance_sheet: ['cash', 'total_assets', 'total_liabilities', 'total_equity'],
  }
  const labels: Record<string, string> = {
    revenue: 'Revenue',
    gross_profit: 'Gross Profit',
    net_income: 'Net Income',
    net_cash_change: 'Net Cash Change',
    cash: 'Cash',
    total_assets: 'Total Assets',
    total_liabilities: 'Total Liabilities',
    total_equity: 'Total Equity',
  }
  const allowed = metricOrder[statementType] || metricOrder.profit_and_loss
  const sourceRows = (response.rows || [])
    .filter((row) => row.scenario === scenario && allowed.includes(row.metric))
    .filter((row) => !req?.as_of_date || monthStart(row.period) > monthStart(req.as_of_date))
  let periods = Array.from(new Set(sourceRows.map((row) => monthStart(row.period)).filter(Boolean))).sort()
  if (Number.isFinite(req?.future_months)) periods = periods.slice(0, Math.max(1, Math.floor(Number(req?.future_months))))
  const byMetric = new Map<string, Map<string, number>>()
  for (const row of sourceRows) {
    const values = byMetric.get(row.metric) || new Map<string, number>()
    values.set(monthStart(row.period), numberValue(row.amount))
    byMetric.set(row.metric, values)
  }
  const generatedAt = bootstrap.last_sync_at || new Date().toISOString()
  const packageId = `canonical-forecast:${bootstrap.accounting_company_id}:${scenario}`
  return {
    contract: contractMeta(bootstrap, 'accounting_statement_forecast', sourceRows.length),
    warnings: periods.length ? [] : ['forecast_not_ready'],
    package: {
      id: packageId,
      workbook_id: bootstrap.workbook_id,
      connection_id: bootstrap.accounting_company_id,
      realm_id: bootstrap.accounting_company_id,
      package_kind: 'single_company_statistical',
      status: 'ready',
      horizon_months: periods.length,
      horizon_weeks: 13,
      start_period: periods[0] || null,
      end_period: periods.at(-1) || null,
      summary_json: { cross_company_learning: false, provider: bootstrap.accounting_provider },
      validation_json: { reconciled_canonical_source: true },
      scenario_key: scenario,
      active_scenario_key: scenario,
      is_active: true,
      created_at: generatedAt,
      updated_at: generatedAt,
    },
    grain,
    statement_type: statementType,
    value_case: valueCase,
    as_of_date: req?.as_of_date || bootstrap.default_period || null,
    statement: {
      connection_id: bootstrap.accounting_company_id,
      layout_version_id: `canonical:${bootstrap.accounting_company_id}`,
      statement_type: statementType,
      balance_sheet_mode: statementType === 'balance_sheet' ? 'point_in_time' : 'period_activity',
      periods,
      rows: allowed.map((metric) => ({
        node_id: metric,
        parent_node_id: null,
        row_type: 'formula',
        statement_type: statementType,
        label: labels[metric] || metric,
        level: 0,
        account_id: null,
        account_number: null,
        cash_flow_section: statementType === 'cash_flow' ? 'operating' : null,
        qbo_account_type: null,
        qbo_account_sub_type: null,
        qbo_classification: null,
        values: periods.map((period) => byMetric.get(metric)?.get(period) ?? null),
        is_total: true,
        is_formula: true,
        normal_balance: metric === 'revenue' ? 'credit' : 'debit',
        semantic_key: metric,
      })),
      warnings: [],
    },
    validation: {
      method: 'single_company_statistical',
      cross_company_learning: false,
      provider: bootstrap.accounting_provider,
    },
    driver_assumptions: {
      method: 'trailing_run_rate_v1',
      editable: false,
    },
  }
}

export function accountingStatementsToValuation(
  bootstrap: AccountingWorkbookBootstrap,
  responses: {
    profit_and_loss: AccountingStatementResponse
    balance_sheet: AccountingStatementResponse
  },
  asOf?: string | null,
): ValuationOverviewResponse {
  const requested = monthStart(asOf)
  const periods = canonicalPeriodMetrics(responses.profit_and_loss, responses.balance_sheet)
    .filter((period) => !requested || period.period <= requested)
  const latest = periods.at(-1)
  const cashByPeriod = new Map<string, number>()
  for (const row of responses.balance_sheet.rows || []) {
    const label = String(row.label || '').toLowerCase()
    const isCash = row.provider_account_type?.toUpperCase() === 'BANK' || /(^|\s)(bank|cash)(\s|$)/.test(label)
    if (isCash) cashByPeriod.set(monthStart(row.period), (cashByPeriod.get(monthStart(row.period)) || 0) + numberValue(row.amount))
  }
  const annualizationBasis = periods.length >= 12 ? 'trailing_twelve' : 'run_rate_times12'
  const flowPeriods = periods.slice(-12)
  const annualize = (selector: (period: CanonicalPeriodMetrics) => number) => {
    const observed = flowPeriods.reduce((sum, period) => sum + selector(period), 0)
    return periods.length >= 12 ? observed : flowPeriods.length ? observed * 12 / flowPeriods.length : 0
  }
  const revenue = annualize((period) => period.revenue)
  const ebitda = annualize((period) => period.revenue - period.directCosts - period.operatingExpenses)
  const netIncome = ebitda
  const assets = latest?.assets || 0
  const debt = latest?.liabilities || 0
  const equity = assets - debt
  const cash = cashByPeriod.get(latest?.period || '') || 0
  const revenueGrowth = periods.length >= 2
    ? nullableRatio((latest?.revenue || 0) - periods.at(-2)!.revenue, Math.abs(periods.at(-2)!.revenue || 0)) || 0
    : 0
  const netMargin = nullableRatio(netIncome, revenue)
  const assumptions: ValuationAssumptions = {
    industry: 'services',
    earnings_multiple: 4,
    ebitda_multiple: 5,
    revenue_multiple: 0.8,
    wacc: 0.18,
    forecast_years: 5,
    forecast_growth: Math.max(-0.1, Math.min(0.2, revenueGrowth)),
    terminal_growth: 0.025,
    annualization_basis: annualizationBasis,
    notes: [
      'Multiples are transparent FuseGrid defaults until an approved industry benchmark is selected.',
      'EBITDA uses operating profit when depreciation and amortization are not separately identifiable.',
      'All inputs come from this company only; no cross-company training is used.',
    ],
  }
  const equityForEnterprise = (enterprise: number) => enterprise - debt + cash
  const method = (id: string, label: string, enterprise: number, weight: number, summary: string) => {
    const mid = equityForEnterprise(enterprise)
    return {
      id,
      label,
      method_summary: summary,
      reconciliation_weight: weight,
      reconciliation_note: 'Provider-neutral deterministic valuation blend.',
      enterprise_value_low: enterprise * 0.8,
      enterprise_value_mid: enterprise,
      enterprise_value_high: enterprise * 1.2,
      enterprise_value: enterprise,
      equity_value_low: equityForEnterprise(enterprise * 0.8),
      equity_value_mid: mid,
      equity_value_high: equityForEnterprise(enterprise * 1.2),
      equity_value: mid,
      inputs: { revenue, ebitda, net_income: netIncome, debt, cash },
      assumptions_text: assumptions.notes,
    }
  }
  const frameworks = [
    method('earnings_multiple', 'Owner earnings', Math.max(0, netIncome) * assumptions.earnings_multiple, 0.3, 'Annualized owner earnings multiplied by a transparent default multiple.'),
    method('ebitda_multiple', 'EBITDA', Math.max(0, ebitda) * assumptions.ebitda_multiple, 0.35, 'Annualized operating earnings multiplied by a transparent default multiple.'),
    method('revenue_multiple', 'Revenue', Math.max(0, revenue) * assumptions.revenue_multiple, 0.2, 'Annualized revenue multiplied by a transparent default multiple.'),
    method('dcf', 'DCF', Math.max(0, netIncome) / Math.max(0.01, assumptions.wacc - assumptions.terminal_growth), 0.15, 'Capitalized sustainable earnings using the disclosed discount and terminal-growth rates.'),
  ]
  const blend = (field: 'equity_value_low' | 'equity_value_mid' | 'equity_value_high') =>
    frameworks.reduce((sum, framework) => sum + numberValue(framework[field]) * numberValue(framework.reconciliation_weight), 0)
  const historyPoints = periods.slice(-12).map((period) => {
    const periodRevenue = period.revenue * 12
    const periodIncome = (period.revenue - period.directCosts - period.operatingExpenses) * 12
    const periodCash = cashByPeriod.get(period.period) || 0
    const methods = [
      { id: 'earnings_multiple', equity_value_low: equityForEnterprise(Math.max(0, periodIncome) * 4 * 0.8), equity_value_mid: equityForEnterprise(Math.max(0, periodIncome) * 4), equity_value_high: equityForEnterprise(Math.max(0, periodIncome) * 4 * 1.2) },
      { id: 'ebitda_multiple', equity_value_low: equityForEnterprise(Math.max(0, periodIncome) * 5 * 0.8), equity_value_mid: equityForEnterprise(Math.max(0, periodIncome) * 5), equity_value_high: equityForEnterprise(Math.max(0, periodIncome) * 5 * 1.2) },
      { id: 'revenue_multiple', equity_value_low: equityForEnterprise(Math.max(0, periodRevenue) * 0.8 * 0.8), equity_value_mid: equityForEnterprise(Math.max(0, periodRevenue) * 0.8), equity_value_high: equityForEnterprise(Math.max(0, periodRevenue) * 0.8 * 1.2) },
      { id: 'dcf', equity_value_low: equityForEnterprise(Math.max(0, periodIncome) / 0.155 * 0.8), equity_value_mid: equityForEnterprise(Math.max(0, periodIncome) / 0.155), equity_value_high: equityForEnterprise(Math.max(0, periodIncome) / 0.155 * 1.2) },
    ]
    const weighted = (field: 'equity_value_low' | 'equity_value_mid' | 'equity_value_high') =>
      methods.reduce((sum, item, index) => sum + numberValue(item[field]) * [0.3, 0.35, 0.2, 0.15][index], 0)
    return {
      period: period.period,
      revenue_ttm: periodRevenue,
      ebitda_ttm: periodIncome,
      net_income_ttm: periodIncome,
      revenue_growth: null,
      ebitda_margin: nullableRatio(periodIncome, periodRevenue),
      net_margin: nullableRatio(periodIncome, periodRevenue),
      total_debt: debt,
      cash: periodCash,
      methods,
      equity_value_low: weighted('equity_value_low'),
      equity_value_mid: weighted('equity_value_mid'),
      equity_value_high: weighted('equity_value_high'),
    }
  })
  return {
    workbook_id: bootstrap.workbook_id,
    connection_id: bootstrap.accounting_company_id,
    as_of: latest?.period || monthStart(bootstrap.default_period) || '',
    contract: contractMeta(bootstrap, 'accounting_valuation', periods.length),
    valuation_set_version: 'accounting_deterministic_v1',
    signature: `accounting-valuation:${bootstrap.accounting_company_id}:${latest?.period || 'empty'}`,
    base_inputs: {
      revenue_ttm: revenue,
      ebitda_ttm: ebitda,
      net_income_ttm: netIncome,
      total_equity: equity,
      total_debt: debt,
      debt_basis: 'total_liabilities_proxy',
      cash,
      revenue_growth: revenueGrowth,
      net_margin: netMargin,
      ebitda_margin: nullableRatio(ebitda, revenue),
      return_on_equity: nullableRatio(netIncome, equity),
      asset_turnover: nullableRatio(revenue, assets),
      annualization_basis: annualizationBasis,
    },
    assumptions,
    frameworks,
    drivers: [
      { id: 'growth', label: 'Revenue growth', current_state: `${(revenueGrowth * 100).toFixed(1)}%`, metric_value: revenueGrowth, metric_unit: 'pct', lever: '+5 pts annual growth', value_impact: revenue * 0.05 * assumptions.revenue_multiple, direction: revenueGrowth >= 0 ? 'positive' : 'negative', explanation: 'Growth changes both the revenue-method value and the earnings base.' },
      { id: 'margin', label: 'Net margin', current_state: `${((netMargin || 0) * 100).toFixed(1)}%`, metric_value: netMargin, metric_unit: 'pct', lever: '+2 pts net margin', value_impact: revenue * 0.02 * assumptions.earnings_multiple, direction: (netMargin || 0) >= 0.1 ? 'positive' : 'negative', explanation: 'Sustainable margin expands owner earnings and cash generation.' },
      { id: 'capital_efficiency', label: 'Asset turnover', current_state: `${(nullableRatio(revenue, assets) || 0).toFixed(2)}x`, metric_value: nullableRatio(revenue, assets), metric_unit: 'times', lever: '+0.1x asset turnover', value_impact: revenue * 0.03, direction: (nullableRatio(revenue, assets) || 0) >= 1 ? 'positive' : 'negative', explanation: 'Higher turnover produces more revenue from the same asset base.' },
      { id: 'risk', label: 'Debt load', current_state: String(debt), metric_value: debt, metric_unit: 'usd', lever: 'Reduce debt', value_impact: -debt, direction: debt > equity ? 'negative' : 'positive', explanation: 'Debt is deducted from enterprise value in the equity bridge.' },
    ],
    equity_value_low: blend('equity_value_low'),
    equity_value_mid: blend('equity_value_mid'),
    equity_value_high: blend('equity_value_high'),
    history: {
      points: historyPoints,
      bridge_mom: null,
      bridge_yoy: null,
      default_weights: [
        { id: 'earnings_multiple', weight: 0.3 },
        { id: 'ebitda_multiple', weight: 0.35 },
        { id: 'revenue_multiple', weight: 0.2 },
        { id: 'dcf', weight: 0.15 },
      ],
    },
    ai_normalization: null,
    warnings: periods.length < 12 ? ['annualized_from_available_history', 'industry_benchmark_not_selected'] : ['industry_benchmark_not_selected'],
  }
}
